import queryParamsFromObject from '../../Client/modules/converter/queryParamsFromObject.js';

const VIN_MESSAGES = {
  notFound: 'We couldn’t fully recognize your VIN',
  noParts: (vehicleLabel) => `We don't offer any parts that fit your ${vehicleLabel}.`,
};

const LICENSE_MESSAGES = {
  noParts: (vehicleLabel) => `We don't offer any parts that fit your ${vehicleLabel}.`,
  notFound: 'Failed to find your license plate number.',
};

const config = {
  baseFields: window.Convermax?.config?.baseFitmentFields,
  extraFields: window.Convermax?.config?.extraFitmentFields,
  YMMTabSelector: 'tab-btn1',
};

export function initHelpers(options = {}) {
  if (options.YMMTabSelector) {
    config.YMMTabSelector = options.YMMTabSelector;
  }

  if (options.baseFields) {
    config.baseFields = options.baseFields;
  }
}

function getSearchEndpoint() {
  return window.Convermax?.templates?.config?.requestConfig?.serverUrl;
}

function addMessage(isGarageDialogOpened, input, message) {
  const oldMsg = document.querySelector('.cm-vin-message');
  if (oldMsg) {
    oldMsg.remove();
  }

  const msg = document.createElement('div');
  msg.className = 'cm-vin-message';
  msg.textContent = message;

  if (isGarageDialogOpened) {
    input.closest('.cm_search-box-root').insertAdjacentElement('afterend', msg);
  } else {
    input.closest('.block-content').insertAdjacentElement('afterend', msg);
  }

  requestAnimationFrame(() => {
    msg.style.opacity = '1';
  });

  input.addEventListener(
    'input',
    () => {
      msg.style.opacity = '0';
      setTimeout(() => msg.remove(), 300);
    },
    { once: true },
  );
}

export function parseVehicleFromFacets(facets, fields) {
  const fieldSet = new Set(fields);

  return facets.reduce(
    (vehicleObj, facet) => {
      if (!fieldSet.has(facet.FieldName)) {
        return vehicleObj;
      }

      if (facet.Selection?.length) {
        vehicleObj.vehicle[facet.FieldName] = facet.Selection[0].Value;
      } else {
        vehicleObj.missingFields.push(facet.FieldName);
      }

      return vehicleObj;
    },
    {
      vehicle: {},
      missingFields: [],
    },
  );
}

function getVinInput(isGarageDialogOpened) {
  return isGarageDialogOpened
    ? document.querySelector('#cm_GarageDialog .cm_vin-search .cm_searchInput')
    : document.querySelector('#cm-vin-searchbox, #tab-sb-vin .block-content');
}

function getLicensePlateInput(isGarageDialogOpened) {
  return isGarageDialogOpened
    ? document.querySelector('#cm_GarageDialog .cm_license-search .cm_searchInput')
    : document.querySelector('#cm-license-plate .cm_searchInput');
}

async function processVehicleSearch(options) {
  const {
    validateInput,
    buildQueryParams,
    getInputElement,
    errorMessages,
    errorMessageKey,
    showMessageOnEmptyVehicle = false,
    fields,
  } = options;

  // Validate input
  if (!validateInput()) {
    return;
  }

  const searchEndpoint = getSearchEndpoint();
  if (!searchEndpoint) {
    return;
  }

  try {
    const queryParams = queryParamsFromObject(buildQueryParams());
    const url = `${searchEndpoint}/search.json?${queryParams}`;

    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Search error: ${response.status} ${response.statusText}`);
    }

    const isGarageDialogOpened = window.document.querySelector('.cm_dialog.GarageDialog .close');
    const { Facets: facets = [], Messages: messages = [] } = await response.json();
    const inputElement = getInputElement(isGarageDialogOpened);

    // Check for specific error message
    if (errorMessageKey && messages.includes(errorMessageKey)) {
      if (inputElement && errorMessages.notFound) {
        addMessage(isGarageDialogOpened, inputElement, errorMessages.notFound);
      }
      return;
    }

    // Build vehicle from facets
    const { vehicle, missingFields } = parseVehicleFromFacets(facets, fields);
    if (Object.keys(vehicle).length === 0) {
      // Fallback if facets are not present
      if (showMessageOnEmptyVehicle && inputElement && errorMessages.notFound) {
        addMessage(isGarageDialogOpened, inputElement, errorMessages.notFound);
      }
      return;
    }

    // Check for missing base fields
    const missingBaseFields = missingFields.filter((f) => config.baseFields.includes(f));
    if (missingBaseFields.length) {
      if (inputElement && errorMessages.noParts) {
        const vehicleLabel = [vehicle.Year, vehicle.Make, vehicle.Model].filter(Boolean).join(' ');
        addMessage(isGarageDialogOpened, inputElement, errorMessages.noParts(vehicleLabel));
      }
      return;
    }

    // Set vehicle and handle navigation
    window.Convermax.discardVehicle();
    window.Convermax.setVehicle(vehicle);

    if (isGarageDialogOpened) {
      const isSearchPage = window.document.body.classList.contains('cm_hasSearchPage');
      const missingExtraFields = missingFields.filter((f) => config.extraFields.includes(f));
      if (missingExtraFields.length || !isSearchPage) {
        window.Convermax.onDialogTabClick('cm_vehicle-search');
      } else {
        window.Convermax.closeGarageDialog();
      }
    } else {
      window.Convermax.onHomeTabClick(config.YMMTabSelector);
    }
  } catch (error) {
    console.error('Search failed:', error);
  }
}

export async function getVinResponse(query, fields) {
  await processVehicleSearch({
    validateInput: () => !!query,
    buildQueryParams: () => ({
      query,
      mode: 'YMM',
      pagesize: '0',
    }),
    getInputElement: getVinInput,
    errorMessages: VIN_MESSAGES,
    errorMessageKey: 'VinVehicleNotFound',
    showMessageOnEmptyVehicle: true,
    fields,
  });
}

export async function getLicensePlateResponse(state, licensePlate, fields) {
  await processVehicleSearch({
    validateInput: () => !!(state && licensePlate),
    buildQueryParams: () => ({
      'query': `state:${state};license plate: ${licensePlate}`,
      'extra.isdifferentialfieldallowed': true,
      'pageSize': 0,
    }),
    getInputElement: getLicensePlateInput,
    errorMessages: LICENSE_MESSAGES,
    errorMessageKey: 'LicensePlateNotFound',
    showMessageOnEmptyVehicle: false,
    fields,
  });
}
